import { getOrganizationDetails } from "./organization-details-services";
import log from '@/lib/logger';
import { getCurrentMileageRate } from "./mileage-rate-services";
import { getPricingSchemes } from "./pricing-services";
import { getServices } from "./service-services";
import { fetchAllSubmissionChannel } from "./submission-channel-services";
import { getMembershipFeeTypes } from "./membership-fee-services";
import { fetchAllMobilityOptions } from "./mobility-option-services";

export interface SettingsCheckResult {
    hasOrganizationDetails: boolean;
    hasActivePricingScheme: boolean;
    hasMileageRate: boolean;
    hasServices: boolean;
    hasSubmissionChannels: boolean;
    hasMembershipFeeTypes: boolean;
    hasMobilityOptions: boolean;
    isComplete: boolean;
    missingCount: number;
}

export async function checkAdminSettings(token: string): Promise<SettingsCheckResult> {
    try {
        // Check all settings in parallel for better performance
        const [orgDetails, mileageRate, pricingSchemes, services, submissionChannels, membershipFeeTypes, mobilityOptions] = await Promise.allSettled([
            getOrganizationDetails(token),
            getCurrentMileageRate(token),
            getPricingSchemes(token),
            getServices(token),
            fetchAllSubmissionChannel(token),
            getMembershipFeeTypes(token),
            fetchAllMobilityOptions(token)
        ]);

        const hasOrganizationDetails = orgDetails.status === "fulfilled" && !!orgDetails.value;
        const hasMileageRate = mileageRate.status === "fulfilled" && !!mileageRate.value;
        
        // Check for active pricing scheme (schema with validFrom <= now)
        let hasActivePricingScheme = false;
        if (pricingSchemes.status === "fulfilled" && pricingSchemes.value) {
            const now = new Date();
            hasActivePricingScheme = pricingSchemes.value.some(scheme => {
                const validFrom = new Date(scheme.validFrom);
                return validFrom <= now;
            });
        }
        
        // Check for services
        let hasServices = false;
        if (services.status === "fulfilled" && services.value) {
            hasServices = services.value.length > 0;
        }

        // Check for submission channels
        let hasSubmissionChannels = false;
        if (submissionChannels.status === "fulfilled" && submissionChannels.value) {
            hasSubmissionChannels = submissionChannels.value.length > 0;
        }

        // Check for membership fee types
        let hasMembershipFeeTypes = false;
        if (membershipFeeTypes.status === "fulfilled" && membershipFeeTypes.value) {
            hasMembershipFeeTypes = membershipFeeTypes.value.length > 0;
        }

        // Check for mobility options
        let hasMobilityOptions = false;
        if (mobilityOptions.status === "fulfilled" && mobilityOptions.value) {
            hasMobilityOptions = mobilityOptions.value.length > 0;
        }

        const missingCount =
            (!hasOrganizationDetails ? 1 : 0) +
            (!hasActivePricingScheme ? 1 : 0) +
            (!hasMileageRate ? 1 : 0) +
            (!hasServices ? 1 : 0) +
            (!hasSubmissionChannels ? 1 : 0) +
            (!hasMembershipFeeTypes ? 1 : 0) +
            (!hasMobilityOptions ? 1 : 0);

        return {
            hasOrganizationDetails,
            hasActivePricingScheme,
            hasMileageRate,
            hasServices,
            hasSubmissionChannels,
            hasMembershipFeeTypes,
            hasMobilityOptions,
            isComplete: missingCount === 0,
            missingCount
        };
    } catch (error) {
        log.error("Error checking admin settings:", error);
        // Return safe defaults on error
        return {
            hasOrganizationDetails: false,
            hasActivePricingScheme: false,
            hasMileageRate: false,
            hasServices: false,
            hasSubmissionChannels: false,
            hasMembershipFeeTypes: false,
            hasMobilityOptions: false,
            isComplete: false,
            missingCount: 7
        };
    }
}